import type { EventId, TemplateView } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';

import GroupInPanel from '../GroupInPanel/GroupInPanel';

import styles from './EventTemplatePanel.scss';
import TemplatePanelTitle from './TemplatePanelTitle';

interface Props {
  competitionGroup: TemplateView[][];
  eventId: EventId;
  groupedOn: 'DATE' | 'COMPETITION';
  isNested: boolean;
  isFlush?: boolean;
}

const EventTemplatePanel: React.FC<Props> = ({
  competitionGroup,
  eventId,
  groupedOn,
  isNested,
  isFlush = false
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const event = useSelector((state: RootState) => widgetSelect.event(state, eventId));
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const panelName = groupedOn === 'DATE' ? event.eventStartTime : competition.competitionName;

  return (
    <div
      className={classNames(styles.panel, {
        [styles.nestedPanel]: isNested,
        [styles.flush]: isFlush
      })}
      key={panelName}
    >
      <ExpandablePanel
        isOpenInitially
        isNested={isNested}
        title={
          <TemplatePanelTitle
            groupedOn={groupedOn}
            competition={competition}
            isNested={isNested}
            panelName={panelName}
          />
        }
      >
        {competitionGroup.map((groupInPanel) => {
          const [marketId] = groupInPanel
            .map((group) =>
              isSingleMarketTemplate(group.template) ? group.template.marketId : undefined
            )
            .filter(notEmpty);

          return (
            <GroupInPanel
              key={`${eventId}_panel}`}
              groupInPanel={groupInPanel}
              marketId={marketId}
            />
          );
        })}
      </ExpandablePanel>
    </div>
  );
};

export default EventTemplatePanel;
